import { Links, Meta, Outlet, Scripts, ScrollRestoration, isRouteErrorResponse } from "react-router";
import type { LinksFunction, MetaFunction } from "react-router";
import { Sentry } from "@/sentry";
import { VersionUpdateBanner } from "@/components/VersionUpdateBanner";
import "@/index.css";

export const links: LinksFunction = () => [
  { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
  { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
  { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
  { rel: "manifest", href: "/manifest.json" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
];

export const meta: MetaFunction = () => [
  { charSet: "UTF-8" },
  { name: "viewport", content: "width=device-width, initial-scale=1.0" },
  { name: "theme-color", content: "#0C1222" },
  { title: "RevHead Pro" },
  { name: "description", content: "RevHead Pro — the app for gearheads. Log your garage, plan meets, and ride with your crew." },
  { property: "og:site_name", content: "RevHead Pro" },
  { property: "og:type", content: "website" },
  { property: "og:url", content: "https://revhead.pro" },
  { property: "og:title", content: "RevHead Pro" },
  { property: "og:description", content: "The app for gearheads. Log your garage, plan meets, and ride with your crew." },
  { property: "og:image", content: "https://revhead.pro/og-default.png" },
  { property: "og:image:width", content: "1200" },
  { property: "og:image:height", content: "630" },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: "RevHead Pro" },
  { name: "twitter:description", content: "The app for gearheads. Log your garage, plan meets, and ride with your crew." },
  { name: "twitter:image", content: "https://revhead.pro/og-default.png" },
];

const THEME_FOUC_SCRIPT = `(function() {
  var mode = localStorage.getItem('gr-theme') || 'system';
  var isLight = mode === 'light' ||
    (mode === 'system' && window.matchMedia('(prefers-color-scheme: light)').matches);
  if (isLight) document.documentElement.classList.add('light');
  document.querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', isLight ? '#F8FAFC' : '#0C1222');
})();`;

const GTM_HEAD_SCRIPT = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-W9LTHZDV');`;

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <Meta />
        <Links />
        {/* GTM — keep above Meta? No: Meta runs per-route and we want GTM as early as possible. Loaded async via the snippet. */}
        <script dangerouslySetInnerHTML={{ __html: "window.dataLayer = window.dataLayer || [];" }} />
        <script dangerouslySetInnerHTML={{ __html: GTM_HEAD_SCRIPT }} />
        {/* Theme FOUC — runs before paint to avoid light/dark flash */}
        <script dangerouslySetInnerHTML={{ __html: THEME_FOUC_SCRIPT }} />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-W9LTHZDV"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <>
      <Outlet />
      <VersionUpdateBanner />
    </>
  );
}

export function ErrorBoundary({ error }: { error: unknown }) {
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : "An unexpected error occurred. Please reload the page.";
  return (
    <div
      style={{
        padding: 40,
        textAlign: "center",
        color: "#E8EDF2",
        background: "#0C1222",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <h1 style={{ fontSize: 24, marginBottom: 12 }}>Something went wrong</h1>
      <p style={{ color: "#7A8FA3", marginBottom: 24 }}>{message}</p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: "10px 24px",
          background: "#4DB8C7",
          color: "#0C1222",
          border: "none",
          borderRadius: 8,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Reload
      </button>
    </div>
  );
}

// Side-effect: register Sentry so it captures errors even when ErrorBoundary doesn't render.
// Sentry.init runs in sentry.ts on import.
void Sentry;
